import { useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  ConditionNodeData,
  ConditionType,
  ComparisonOperator,
} from '../../../../nodes/condition-node/condition.node'
import type { BuilderNodeType } from '../../../../types'

type ConditionNodePropertyPanelProps = Readonly<{
  id: string
  type: BuilderNodeType
  data: ConditionNodeData
  updateData: (data: Partial<ConditionNodeData>) => void
}>

const CONDITION_TYPES: { value: ConditionType; label: string }[] = [
  { value: 'tags', label: 'Etiquetas' },
  { value: 'fields', label: 'Campos personalizados' },
  { value: 'values', label: 'Valores' },
]

const OPERATORS: { value: ComparisonOperator; label: string }[] = [
  { value: 'eq', label: 'Igual' },
  { value: 'gt', label: 'Maior' },
  { value: 'lt', label: 'Menor' },
  { value: 'contains', label: 'Contém' },
]

export default function ConditionNodePropertyPanel({
  id,
  data,
  updateData,
}: ConditionNodePropertyPanelProps) {
  const current = useMemo(() => data, [data])
  const leftFieldOptions = useMemo(() => {
    if (current.conditionType === 'tags') {
      return [
        { value: 'contact.tags', label: 'contact.tags' },
        { value: 'ticket.tags', label: 'ticket.tags' },
      ]
    }
    if (current.conditionType === 'fields') {
      return [
        { value: 'custom.category', label: 'custom.category' },
        { value: 'custom.score', label: 'custom.score' },
        { value: 'custom.assignee', label: 'custom.assignee' },
      ]
    }
    return [
      { value: 'ticket.status', label: 'ticket.status' },
      { value: 'ticket.priority', label: 'ticket.priority' },
      { value: 'contact.name', label: 'contact.name' },
      { value: 'contact.phone', label: 'contact.phone' },
      { value: 'user.name', label: 'user.name' },
    ]
  }, [current.conditionType])

  return (
    <div className='flex flex-col gap-4 p-4'>
      <div className='-mb-4 self-end text-xs text-card-foreground/20'>{id}</div>

      <div className='grid grid-cols-1 gap-3'>
        <div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
          <div className='col-span-1'>
            <Label className='mb-1 block text-xs'>Tipo de condição</Label>
            <Select
              value={current.conditionType}
              onValueChange={(v: ConditionType) =>
                updateData({ conditionType: v })
              }
            >
              <SelectTrigger className='h-8'>
                <SelectValue placeholder='Selecione' />
              </SelectTrigger>
              <SelectContent>
                {CONDITION_TYPES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='col-span-1 sm:col-span-2'>
            <Label className='mb-1 block text-xs'>Campo/Origem (esq.)</Label>
            <Select
              value={current.left}
              onValueChange={(v: string) => updateData({ left: v })}
            >
              <SelectTrigger className='h-8'>
                <SelectValue placeholder='Selecione o campo/origem' />
              </SelectTrigger>
              <SelectContent>
                {leftFieldOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
          <div className='col-span-1'>
            <Label className='mb-1 block text-xs'>Operador</Label>
            <Select
              value={current.operator}
              onValueChange={(v: ComparisonOperator) =>
                updateData({ operator: v })
              }
            >
              <SelectTrigger className='h-8'>
                <SelectValue placeholder='Selecione' />
              </SelectTrigger>
              <SelectContent>
                {OPERATORS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='col-span-1 sm:col-span-2'>
            <Label className='mb-1 block text-xs'>Comparar com (dir.)</Label>
            <Input
              value={current.right}
              onChange={(e) => updateData({ right: e.target.value })}
              placeholder='Ex.: aberto, vip, 10, {now}'
            />
          </div>
        </div>

        <div className='text-xs text-card-foreground/60'>
          Saídas: verde = verdadeiro, vermelho = falso
        </div>
      </div>
    </div>
  )
}
